import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, ChevronDown } from 'lucide-react';

// import { handleCodeImport, handleThemeImport } from '../utils/helper';

interface ApiClientOption {
  value: string;
  label: string;
  isDefault?: boolean;
}

const frameworkApiClients: Record<string, ApiClientOption[]> = {
  FLUTTER: [
    {
      value: 'HTTP',
      label: 'HTTP',
      isDefault: true,
    },
  ],
  DEFAULT: [
    {
      value: 'Fetch',
      label: 'Fetch',
      isDefault: true,
    },
    {
      value: 'Axios',
      label: 'Axios',
    },
  ],
};

interface CodeIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onSpecUpdate: (spec: string) => void;
  onSuccessIconUpdate: () => void;

  /*
   * onCodeFilesUpdate: (files: Array<{ name: string; content: string }>) => void;
   * onThemeFileUpdate: (file: { name: string; content: string }) => void;
   * codeFiles: Array<{ name: string; content: string }>;
   * themeFile: { name: string; content: string };
   */
  selectedFramework: string;
}

const CodeIntegration: React.FC<CodeIntegrationProps> = ({
  isOpen,
  onClose,
  onSpecUpdate,
  onSuccessIconUpdate,

  /*
   * codeFiles,
   * onCodeFilesUpdate,
   * onThemeFileUpdate,
   * themeFile,
   */
  selectedFramework,
}) => {
  const [selectedApi, setSelectedApi] = useState<string>('Fetch');
  const [openApiSpec, setOpenApiSpec] = useState('');
  const [selectedOption, setSelectedOption] = useState<'infer' | 'paste'>('paste');
  const [giveYamlSpec, setGiveYamlSpec] = useState(false);

  const isFrameworkSupported = false;
  useEffect(() => {
    console.log('[openapi] selectedFramework', selectedFramework);

    if (!localStorage.getItem('dualite_api_call_method')) {
      if (selectedFramework !== 'flutter') {
        localStorage.setItem('dualite_api_call_method', 'Fetch');
      } else {
        localStorage.setItem('dualite_api_call_method', 'HTTP');
      }
    }
  }, []);

  /*
   * const handleImportTheme = (e: React.ChangeEvent<HTMLInputElement>) => {
   *   handleThemeImport(e, onThemeFileUpdate)
   * }
   * const handleImportCode = (e: React.ChangeEvent<HTMLInputElement>) => {
   *   handleCodeImport(e, codeFiles, onCodeFilesUpdate)
   * }
   */

  const handleSelectApi = (api: string) => {
    setSelectedApi(api);
    console.log('api', api);
    localStorage.setItem('dualite_api_call_method', api);
  };

  const handleSpecChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOpenApiSpec(e.target.value);
  };

  const handleSave = () => {
    /*
     * Save code files to localStorage
     * localStorage.setItem('importedCodeFiles', JSON.stringify(codeFiles))
     * Save theme file to localStorage if it exists
     * if (themeFile?.name && themeFile?.content) {
     *   localStorage.setItem('importedThemeFile', JSON.stringify(themeFile))
     * }
     */

    if (selectedOption === 'infer') {
      onSpecUpdate('DUALITEX_INFER_YOURSELF');
      localStorage.setItem('dualite_api_data', 'DUALITEX_INFER_YOURSELF');

      // yaml
      if (giveYamlSpec) {
        localStorage.setItem('dualite_api_data_yaml', 'true');
      }

      if (selectedFramework !== 'flutter') {
        localStorage.setItem('dualite_api_call_method', 'Fetch');
      } else {
        localStorage.setItem('dualite_api_call_method', 'HTTP');
      }
    } else {
      console.log('openApiSpec', openApiSpec);
      onSpecUpdate(openApiSpec);
      localStorage.setItem('dualite_api_data', openApiSpec);

      if (selectedFramework !== 'flutter') {
        localStorage.setItem('dualite_api_call_method', selectedApi);
      } else {
        localStorage.setItem('dualite_api_call_method', 'HTTP');
      }
    }

    // Call onSuccessIconUpdate before closing
    onSuccessIconUpdate();

    /*
     * Delay closing to show success icon
     * setTimeout(() => {
     */
    onClose();

    // }, 2000)
  };

  // Get API client options based on framework
  const getApiClientOptions = () => {
    console.log('selectedFramework >>>>', selectedFramework);

    if (selectedFramework.toLowerCase().includes('flutter')) {
      return frameworkApiClients.FLUTTER;
    }

    return frameworkApiClients.DEFAULT;
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center font-inter">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative w-[500px] rounded-xl bg-transparent">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-lg  p-2 text-[#fff] hover:text-[#E7E7E7] transition-colors duration-200"
        >
          <span className="text-3xl leading-none font-light">&times;</span>
        </button>

        <div className="bg-alpha-background-secondary p-3 rounded-xl">
          <div className="max-w-3xl p-4">
            <div className="flex flex-col space-y-3">
              <div className="flex flex-col">
                <h2 className="text-[16px] text-white font-semibold">Code Integration</h2>
                <p className="text-[12px] text-alpha-text-heading-start mb-2">
                  Select your preferred method of API integration
                </p>
              </div>

              <div
                className={`p-5 rounded-lg border cursor-pointer
                  ${
                    selectedOption === 'infer'
                      ? 'border-[#2C4DF1] shadow-[0_0_10px_rgba(44,77,241,0.25)]'
                      : 'border-[#414142]'
                  }`}
                onClick={() => setSelectedOption('infer')}
              >
                <div className="flex justify-between items-start">
                  <div className="flex flex-col gap-1">
                    <p className="text-[15px] text-white font-medium">Infer API Specification Automatically</p>
                    <p className="text-[12px] text-alpha-text-heading-start ">
                      Alpha analyses your UI and requirements, and infers a relevant API specification and integrates
                      the API accordingly.
                    </p>
                  </div>
                </div>

                {selectedOption === 'infer' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-3 pt-3 border-t border-[#414142]"
                  >
                    <label
                      className="flex items-center gap-3 cursor-pointer group"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="relative flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={giveYamlSpec}
                          onChange={(e) => setGiveYamlSpec(e.target.checked)}
                          className="sr-only" // Hide the actual checkbox but keep it accessible
                        />
                        <div
                          className={`w-5 h-5 rounded border ${
                            giveYamlSpec
                              ? 'bg-[#2C4DF1] border-[#2C4DF1]'
                              : 'border-[#60606180] bg-[#404040] group-hover:border-[#606060]'
                          } flex items-center justify-center transition-colors`}
                        >
                          {giveYamlSpec && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3.5 w-3.5 text-white"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm text-[#B5B5B6] group-hover:text-[#E7E7E7] transition-colors">
                        Generate a YAML file containing the infered specification
                      </span>
                    </label>
                  </motion.div>
                )}
              </div>

              <div
                className={`p-5 rounded-lg border cursor-pointer
                  ${
                    selectedOption === 'paste'
                      ? 'border-[#2C4DF1] shadow-[0_0_10px_rgba(44,77,241,0.25)]'
                      : 'border-[#414142]'
                  }`}
                onClick={() => setSelectedOption('paste')}
              >
                <p className="text-[15px] text-white font-medium ">Paste your existing API Specification</p>
                <p className="text-[12px] text-alpha-text-heading-start mb-2 ">
                  Use your OpenAPI or a Swagger specification
                </p>

                {selectedOption === 'paste' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col space-y-3"
                  >
                    <div className="relative">
                      <select
                        value={selectedApi}
                        onChange={(e) => handleSelectApi(e.target.value)}
                        className="w-full h-9 px-3 flex items-center justify-between
                        bg-alpha-background-secondary
                        text-alpha-text-muted
                        hover:text-[var(--alpha-text-white)]
                        text-left text-sm font-[var(--alpha-font-family-display)]
                        border border-[#414142]
                        rounded-[var(--alpha-radius-lg)]
                        appearance-none cursor-pointer
                        transition-colors duration-200"
                      >
                        {!selectedApi && <option value="">Select your API call</option>}
                        {getApiClientOptions().map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                            className="bg-[var(--alpha-background-editor-primary)] text-[var(--alpha-text-white)]"
                          >
                            {option.label} {option.isDefault && '(Default)'}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none text-[var(--alpha-text-muted)]" />
                    </div>

                    <textarea
                      placeholder="Paste your OpenAPI Specification here..."
                      value={openApiSpec === 'INFER_YOURSELF' ? '' : openApiSpec}
                      onChange={handleSpecChange}
                      className="w-full h-[120px] p-3 bg-[#1D1D1D] text-[#B5B5B6]
                        border border-[#414142] rounded-lg
                        placeholder-[#929292]  text-sm
                        focus:outline-none focus:border-[#2C4DF1]"
                    />
                  </motion.div>
                )}
              </div>

              <hr className="my-4 border-[#414142]" />

              <div>
                <div className="mb-4 p-4 bg-[#1D1D1D20] border border-[#414142] rounded-lg">
                  <h3 className="text-sm font-medium text-alpha-text-heading-start mb-2">
                    What is an OpenAPI/Swagger Specification?
                  </h3>
                  <p className="text-xs text-alpha-text-heading-start">
                    Including your API spec helps us generate more accurate code for your application.
                    <a
                      href="https://swagger.io/solutions/getting-started-with-oas/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2C4DF1] hover:underline ml-1"
                    >
                      Learn more about API Specification
                    </a>
                  </p>
                </div>
              </div>

              {/* <span className="text-alpha-text-heading-start text-sm">Codebase Connect</span> */}

              {/* <div className="relative">
                <label
                  className={`flex items-center px-3 h-9 border border-[#60606140] 
                  rounded-lg text-[#929292] cursor-pointer
                  ${!isFrameworkSupported && 'opacity-50 cursor-not-allowed'}
                  hover:bg-[#333333] hover:text-[#E7E7E7]`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  <span className="text-sm ">Import your codebase</span>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept=".js,.ts,.jsx,.tsx"
                    disabled={!isFrameworkSupported}
                  />
                </label>
                {!isFrameworkSupported && (
                  <span
                    className="absolute right-2 top-1/2 -translate-y-1/2
                    px-2 py-1 text-xs text-white bg-[#383838] rounded-full"
                  >
                    Coming Soon
                  </span>
                )}
              </div>

              <div className="relative">
                <label
                  className={`flex items-center px-3 h-9 border border-[#60606140] 
                  rounded-lg text-[#929292]
                  ${!isFrameworkSupported && 'opacity-50 cursor-not-allowed'}
                  ${isFrameworkSupported && 'cursor-pointer hover:bg-[#333333] hover:text-[#E7E7E7]'}`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  <span className="text-sm ">Import your theme</span>
                  <input type="file" className="hidden" accept=".js,.ts" disabled={!isFrameworkSupported} />
                </label>
                {!isFrameworkSupported && (
                  <span
                    className="absolute right-2 top-1/2 -translate-y-1/2
                    px-2 py-1 text-xs text-white bg-[#383838] rounded-full"
                  >
                    Coming Soon
                  </span>
                )}
              </div> */}

              <button
                onClick={handleSave}
                disabled={!openApiSpec.trim() && selectedOption !== 'infer'}
                className="flex items-center justify-center gap-[6px] w-full py-[10px] bg-gradient-to-b from-alpha-primary-start to-alpha-primary-end hover:brightness-94 border-none rounded-lg text-white text-[13px] font-medium cursor-pointer transition-all duration-200 ease hover:bg-[#1d4ed8] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeIntegration;
